import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, Loader2, Plus, Shield, ShieldAlert } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import insuranceService from '../../services/equipmentInsurance';
import equipmentService from '../../services/equipment';
import type { InsurancePolicy } from '../../types/equipmentInsurance';
import type { Equipment } from '../../types/equipment';
import { categoryOf, isRoadVehicleCategory } from '../../types/equipment';
import {
  COVER_TYPE_LABELS, TONE_CLASSES, insuranceScheduleCsv, insuranceState,
} from '../../utils/equipmentInsurance';
import { downloadBlob } from '../../utils/downloadBlob';
import { formatLocalDateInTz, getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { cn } from '../../utils/cn';

function machineSummary(p: InsurancePolicy): string {
  const names = (p.items ?? []).map(i => i.equipment_name);
  if (!names.length) return 'No machines listed';
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/**
 * /equipment/insurance — every policy over the register, soonest expiry first,
 * and the road vehicles on none. Anyone can read it; adding and renewing take
 * a vehicles creator or admin seat.
 */
export default function InsurancePolicies() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const { hasAccess: canManage } = useModuleLicense('vehicles', ['creator', 'admin']);
  const [showEnded, setShowEnded] = useState(false);
  const [policies, setPolicies] = useState<InsurancePolicy[] | null>(null);
  const [machines, setMachines] = useState<Equipment[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    insuranceService.list(showEnded)
      .then(res => {
        if (res.success && res.data) setPolicies(res.data);
        else setError('Could not load the policies.');
      })
      .catch(() => setError('Could not load the policies.'));
  }, [showEnded]);

  useEffect(() => {
    equipmentService.list()
      .then(res => { if (res.success && res.data) setMachines(res.data); })
      .catch(() => { /* the uninsured list is a convenience */ });
  }, []);

  const current = useMemo(() => (policies ?? []).filter(p => p.status === 'current'), [policies]);
  const expired = current.filter(p => insuranceState(p).tone === 'expired').length;
  const due = current.filter(p => ['urgent', 'due'].includes(insuranceState(p).tone)).length;

  // Road vehicles on no current policy and not marked as needing none — the
  // same rule as the dashboard card's "No insurance".
  const uninsured = useMemo(() => {
    if (!policies) return [];
    const covered = new Set(current.flatMap(p => (p.items ?? []).map(i => i.equipment_id)));
    return machines.filter(m =>
      m.status !== 'retired' && !m.insurance_not_required && !covered.has(m.id)
      && isRoadVehicleCategory(categoryOf(m)));
  }, [machines, current, policies]);

  function downloadSchedule() {
    const csv = insuranceScheduleCsv(current);
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `insurance-schedule-${getTodayInTz(orgTz)}.csv`);
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Insurance"
        subtitle="Policies over your equipment"
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={downloadSchedule} disabled={!current.length}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Asset schedule
            </Button>
            {canManage && (
              <Button size="sm" onClick={() => navigate('/equipment/insurance/new')}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add policy
              </Button>
            )}
          </div>
        }
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {policies === null && !error ? (
          <p className="text-sm text-neutral-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : policies && (
          <>
            {(expired > 0 || due > 0) && (
              <div className={cn(
                'rounded-xl border px-4 py-3 text-sm',
                expired > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800',
              )}>
                {expired > 0 && <p>{expired} polic{expired > 1 ? 'ies have' : 'y has'} lapsed. Those machines are not covered until renewed.</p>}
                {due > 0 && <p>{due} polic{due > 1 ? 'ies expire' : 'y expires'} within 30 days.</p>}
              </div>
            )}

            <label className="flex items-center gap-2 text-sm text-neutral-600">
              <input type="checkbox" checked={showEnded} onChange={e => setShowEnded(e.target.checked)} />
              Show renewed and cancelled policies
            </label>

            {policies.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center">
                  <Shield className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
                  <p className="text-neutral-700 font-medium">No insurance policies recorded</p>
                  <p className="text-sm text-neutral-500 mt-1 max-w-sm mx-auto">
                    Add a policy and the machines it covers. You will be reminded a month and a week before it expires.
                  </p>
                  {canManage && (
                    <Button size="sm" className="mt-4" onClick={() => navigate('/equipment/insurance/new')}>
                      Add policy
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <ul className="space-y-2">
                {policies.map(p => {
                  const st = insuranceState(p);
                  return (
                    <li key={p.id}>
                      <Link
                        to={`/equipment/insurance/${p.id}`}
                        className="block bg-white rounded-xl border border-neutral-200 px-4 py-3 hover:border-neutral-300 hover:shadow-sm"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-neutral-800 break-words">{p.insurer}</p>
                            <p className="text-sm text-neutral-600 break-all">
                              {p.policy_number} · {COVER_TYPE_LABELS[p.cover_type] ?? p.cover_type}
                            </p>
                          </div>
                          <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0', TONE_CLASSES[st.tone])}>
                            {st.label}
                          </span>
                        </div>
                        <p className="text-sm text-neutral-600 mt-1">
                          Expires {formatLocalDateInTz(p.expiry_date, orgTz)}
                        </p>
                        <p className="text-xs text-neutral-500 mt-1 break-words">{machineSummary(p)}</p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}

            {uninsured.length > 0 && policies.length > 0 && (
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium text-neutral-800 flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-amber-500" />
                    Road vehicles on no policy
                  </p>
                  <p className="text-xs text-neutral-500 mt-1">
                    Add them to a policy, or mark one as not needing insurance from its own page.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {uninsured.map(m => (
                      <li key={m.id}>
                        <Link to={`/equipment/${m.id}`} className="text-sm text-primary-600 hover:underline break-words">
                          {m.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}
